/* eslint-disable tailwindcss/no-arbitrary-value */
import Spinner from '@hyphengroup/cms-components/components/Spinner/Spinner';
import classNames from 'classnames';
import { staticText } from 'utils/staticText';
import { TIEmptyResults } from './EmptyResults';
import { TIErrorResult } from './ErrorResult';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

const Translations = staticText({
  searching: {
    _: 'Searching',
    'zh-hk': '搜尋中',
  },
  stillSearching: {
    _: 'Still searching…',
    'zh-hk': '繼續搜尋中…',
  },
  results: {
    _: 'results',
    'zh-hk': '個結果',
  },
  noResults: {
    _: '0 of :total results',
    'zh-hk': '0/:total 個結果',
  },
  error: {
    _: '0 results (error)',
    'zh-hk': '搜尋時發生錯誤。請稍後再試。',
  },
});

export const TISearchFooter = ({
  className,
  inProgress,
  hasError,
  resultCount,
  onRetry,
  /**
   * The Sort & Filter panel prints the count itself, so the settled count is
   * dropped rather than printed a second time a line above it. The spinner,
   * the error and the empty state stay: nothing else reports those.
   */
  hideCount,
}: {
  className?: string;
  resultCount?: number;
  inProgress?: boolean;
  hasError?: boolean;
  onRetry?: () => void;
  hideCount?: boolean;
}) => {
  const context = useClientContext();
  const staticText = getClientTranslations(Translations, context);

  const showError = !inProgress && hasError;
  const showCount = !inProgress && !hasError && !hideCount;
  const showNoResults = !inProgress && !hasError && !resultCount;
  const showStatusLine = inProgress || showCount || showError;
  // `hideCount` can leave a settled search with nothing to report. The hook
  // below still renders, so the count stays readable, but it must not keep the
  // caller's margin and the column gap alive around nothing.
  const isEmpty = !showStatusLine && !showNoResults;

  return (
    // eslint-disable-next-line tailwindcss/no-custom-classname
    <div
      // Test-hook contract (CMS-1878). Waiting is done on `data-settled`
      // (TravelPolicySearch); this is the count once it has.
      data-testid="ti-quote-count"
      data-count={resultCount ?? 0}
      className={
        isEmpty
          ? undefined
          : classNames(className, 'flex w-full flex-col gap-spacing/gap/y-sm')
      }
    >
      {showStatusLine && (
        <div className="mhg-alpha-typography-body-2 flex align-middle">
          {inProgress && (
            <>
              <Spinner className="mr-spacing/gap/soft-6px size-[18px]" />
              <span>
                {resultCount ? staticText.stillSearching : staticText.searching}
              </span>
            </>
          )}
          {showCount && <span>{`${resultCount} ${staticText.results}`}</span>}
          {showError && <span>{staticText.error}</span>}
        </div>
      )}
      {showNoResults && <TIEmptyResults />}
      {showError && <TIErrorResult onRetry={onRetry} />}
    </div>
  );
};
